<template>
  <template v-if="canSeeNotes || isDealsList">
    <teleport
      v-if="dealNotesOptions.state === NoteState.Draggable"
      to="#modals"
    >
      <vue-resizable
        data-cy="deal-notes-draggable"
        class="z-[101]"
        drag-selector=".handle"
        :width="dealNotesOptions.width"
        :height="dealNotesOptions.height"
        :top="dealNotesOptions.y"
        :left="dealNotesOptions.x"
        :min-width="notesWidthAndHeight.minWidth"
        :min-height="notesWidthAndHeight.minHeight"
        @mount="updatePosition"
        @resize:end="updatePosition"
        @drag:end="updatePosition"
      >
        <div
          ref="draggable"
          class="w-full rounded-md border border-gray-300 bg-white shadow-lg"
          :class="
            dealNotesOptions.minimized ? 'h-[55px]' : 'h-full overflow-hidden'
          "
        >
          <div
            ref="handle"
            data-cy="handle"
            class="handle absolute left-4 top-[18px]"
          >
            <icon-base
              :icon="IconCardView"
              width="16"
              height="16"
              view-box="0 0 24 24"
              class="handle rotate-90 transform cursor-grab"
            />
          </div>
          <notes-content ref="draggableNotes" :notes-loading="loading" />
        </div>
      </vue-resizable>
    </teleport>
    <div
      v-else-if="dealNotesOptions.state === NoteState.Static"
      id="dealsNotesWrapper"
      data-cy="deal-notes-static"
      class="z-[99] h-screen w-full bg-white pt-4 shadow-2xl md:w-1/3 lg:w-1/4"
    >
      <notes-content :notes-loading="loading" @popover:init="initPosition" />
    </div>
  </template>
</template>
<script lang="ts">
import { useDeals } from "@/hooks/deals";
import { usePromiseWrapper } from "@/hooks/common";
import {
  computed,
  reactive,
  ref,
  watch,
  onMounted,
  onBeforeUnmount
} from "vue";
import queryString from "query-string";
import { useNotification } from "@/hooks/notifications";
import { useLocalStorageSetting } from "@/hooks/options";
import { useI18n } from "vue-i18n";
import useApplicationsStore from "@/stores/applications";
import useNotesStore from "@/stores/notes";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { DEFAULT_PER_PAGE } from "@/helpers/constants";
import { DEFAULT_DEAL_NOTES_OPTIONS } from "@/helpers/constants/deals";
import { ensurePositionWithinViewport } from "@/helpers/elements";
import type { DealNotesOptions, ResizableEventValues } from "@/models/common";
import { NoteState } from "@/enums/notes";
import { ROUTE_DEALS } from "@/router/routes";
import throttle from "lodash/throttle";
</script>
<script setup lang="ts">
import NotesContent from "@/components/deals/NotesContent.vue";
import IconCardView from "@/components/icons/IconCardView.vue";
import VueResizable from "vue-resizable";

const initialParams = queryString.parse(location.search);
const { activeDeal } = useDeals();
const applicationsStore = useApplicationsStore();
const notesStore = useNotesStore();
const { showMessage } = useNotification();
const { t } = useI18n();
const route = useRoute();
const dealNotesOptions = useLocalStorageSetting(
  "dealNotesOptions",
  DEFAULT_DEAL_NOTES_OPTIONS
);

const { shouldFetchNotes, canSeeNotes } = storeToRefs(applicationsStore);
const { currentNotes: dealNotes } = storeToRefs(notesStore);
const { loading, fetchWrapper: getNotes } = usePromiseWrapper(
  async () => {
    await notesStore.getNotes({
      application_ids: [activeDealId.value],
      per_page: DEFAULT_PER_PAGE
    });
  },
  { onError: () => showMessage(t("COMMON.ACCESS_RESTRICTED"), "error") }
);

const draggable = ref<HTMLElement | null>(null);
const draggableNotes = ref<InstanceType<typeof NotesContent> | null>(null);
const handle = ref<HTMLElement | null>(null);
// min values are the smallest the component can go
// and still be practically usable
// width and height are initial values
const notesWidthAndHeight = ref({
  width: DEFAULT_DEAL_NOTES_OPTIONS.width,
  height: DEFAULT_DEAL_NOTES_OPTIONS.height,
  minWidth: 270,
  minHeight: 250
});

const params = reactive({
  note: (initialParams.note || null) as string | unknown
});

const numOfNotes = computed(() => dealNotes.value?.data?.length);
const activeDealId = computed(() => activeDeal.value.id);
const isDealsList = computed(() => route.name === ROUTE_DEALS);

const initPosition = (position: Pick<DealNotesOptions, "x" | "y">) =>
  updatePosition(
    {
      left: position.x,
      top: position.y,
      width: notesWidthAndHeight.value.width,
      height: notesWidthAndHeight.value.height
    },
    true
  );

const updatePosition = async (
  positionAndEvent: ResizableEventValues,
  init = false
) => {
  // if we are initializing the draggable from the static version
  // we need to offset x and y a bit for a clean position of the draggable
  dealNotesOptions.value = {
    ...dealNotesOptions.value,
    x: positionAndEvent.left - (init ? 150 : 0),
    y: positionAndEvent.top + (init ? 50 : 0),
    width: positionAndEvent.width,
    height: positionAndEvent.height
  };
  await repositionAsNeeded();
};

const repositionAsNeeded = async () => {
  const draggableBounding = draggable.value?.getBoundingClientRect();
  const updates = await ensurePositionWithinViewport(
    dealNotesOptions.value,
    draggableBounding
  );
  dealNotesOptions.value = {
    ...dealNotesOptions.value,
    x: updates.x,
    y: updates.y
  };
};

const scrollNoteIntoView = () => {
  const note = document.getElementById(params.note as string) as HTMLDivElement;
  if (!note) {
    return;
  }
  note.scrollIntoView({
    block: "start",
    inline: "nearest",
    behavior: "smooth"
  });
  setTimeout(() => {
    note.classList.add("highlight");
  }, 300);
  setTimeout(() => {
    note.classList.remove("highlight");
  }, 3000);
};

const resizeListener = throttle(
  async () => {
    repositionAsNeeded();
  },
  100,
  { leading: false }
);

watch(
  [activeDealId, canSeeNotes, dealNotesOptions],
  async () => {
    if (
      !activeDealId.value ||
      !canSeeNotes.value ||
      dealNotesOptions.value.state === NoteState.Closed ||
      !shouldFetchNotes.value
    ) {
      return;
    }
    applicationsStore.updateFetchServiceStatus(["notes"], false);
    getNotes();
  },
  { deep: true, immediate: true }
);

watch(
  [activeDeal, params, loading, numOfNotes],
  () => {
    if (activeDeal.value && params.note && numOfNotes.value) {
      const isNoteInDeal = dealNotes.value?.data?.some(
        (note) => note.id === Number(params.note)
      );
      if (!isNoteInDeal) {
        return;
      }
      if (dealNotesOptions.value.state === NoteState.Draggable) {
        return;
      }
      if (!loading.value && dealNotesOptions.value.state !== NoteState.Closed) {
        scrollNoteIntoView();
      }
    }
  },
  { immediate: true }
);

onMounted(() => {
  window.addEventListener("resize", resizeListener);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", resizeListener);
});
</script>
<style scoped>
#dealsNotesWrapper {
  container-type: inline-size;
  container-name: deal-notes-wrapper;
}

@container deal-notes-wrapper (min-width: 400px) {
  :deep() {
    .note-type-menu {
      @apply left-[88px];
    }
  }
}
</style>
